import React, { useEffect, lazy, Suspense, useRef } from 'react';
import * as Sentry from '@sentry/react';
import {
    Route,
    Routes,
    useLocation,
} from 'react-router-dom';
import { fetchGetQuery } from '/src/api';
import {
    useAuthContext,
    useCartContext,
    useModalsContext,
    useWishListsContext,
} from '/src/providers';
import { Loader } from '/src/components';
import { COOKIE_CONSENT } from '/src/consts';
import {
    intercomLoginUser,
    intercomLogoutUser,
} from '/src/services';
import { getCookie } from '/src/utils';

const Home = lazy(() => import(/* webpackChunkName: "home" */ '/src/view/Home'));
const Search = lazy(() => import(/* webpackChunkName: "search" */ '/src/view/Search'));
const Basket = lazy(() => import(/* webpackChunkName: "basket" */ '/src/view/Basket'));
const Recipes = lazy(() => import(/* webpackChunkName: "recipes" */ '/src/view/Recipes'));
const Checkout = lazy(() => import(/* webpackChunkName: "checkout" */ '/src/view/Checkout'));
const Customer = lazy(() => import(/* webpackChunkName: "customer" */ '/src/view/Customer'));
const UrlResolver = lazy(() => import(/* webpackChunkName: "url-resolver" */ '/src/view/UrlResolver'));
const WishListShared = lazy(() => import(/* webpackChunkName: "wish-list-shared" */ '/src/view/WishListShared'));
const ResetPassword = lazy(() => import(/* webpackChunkName: "reset-password" */ '/src/view/ResetPassword'));
const EmailPreferences = lazy(() => import(/* webpackChunkName: "email-preferences" */ '/src/view/EmailPreferences'));

const App = () => {
    const { isAuth } = useAuthContext();
    const {
        cart,
        isCart,
        getCustomerCart,
        getCart,
        clearCart,
        isCartItems,
        getLocalCartId,
        removeLocalCartId,
    } = useCartContext();
    const { pathname, search } = useLocation();
    const { showModal, MODALS_TYPES } = useModalsContext();
    const { isWishLists, clearWishLists, getWishLists } = useWishListsContext();
    const ref = useRef(isAuth);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [pathname, search]);

    useEffect(() => {
        if (isAuth) {
            const guestCart = cart;

            getCustomerCart()
                .then(({ customerCart: { is_cart_merged } }) => {
                    if (getLocalCartId()) {
                        removeLocalCartId();
                    }

                    if (guestCart.id && isCartItems && !is_cart_merged) {
                        showModal(MODALS_TYPES.MERGE_CARTS, { guestCart })
                    }
                }).catch(() => {});

            getWishLists().catch(() => {});
        } else {
            if (isCart) {
                clearCart();
            } else if (getLocalCartId()) {
                getCart(getLocalCartId())
                    .catch(() => removeLocalCartId());
            }

            if (isWishLists) {
                clearWishLists();
            }
        }
    }, [isAuth]);

    useEffect(() => {
        if (isAuth) {
            fetchGetQuery({
                query: `{ customer { email firstname lastname } }`,
            }).then(({ customer: { email, firstname, lastname } }) => {
                Sentry.setUser({ email });

                if (ref.current) {
                    intercomLoginUser({ email, name: `${firstname} ${lastname}` })
                }
            }).catch(() => {});
        } else {
            if (ref.current !== isAuth) {
                intercomLogoutUser();
            }
        }

        ref.current = isAuth;
    }, [isAuth]);

    useEffect(() => {
        if (!getCookie(COOKIE_CONSENT)) {
            showModal(MODALS_TYPES.COOKIE_CONSENT);
        }
    }, []);

    return (
        <Suspense fallback={<Loader />}>
            <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/search" element={<Search />} />
                <Route path="/basket" element={<Basket />} />
                <Route path="/recipes" element={<Recipes />} />
                <Route path="/checkout/*" element={<Checkout />} />
                <Route path="/customer/*" element={<Customer />} />
                <Route path="/wish-list/shared/:id" element={<WishListShared />} />
                <Route path="/customer/account/createPassword" element={<ResetPassword />} />
                <Route path="/emailpreferences/edit" element={<EmailPreferences />} />
                <Route path="*" element={<UrlResolver />} />
            </Routes>
        </Suspense>
    )
};

export default App;
